import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/**
 * Review of incoming LIS results: a laboratory result is held as PENDING and
 * reaches the form only when a person approves it. Tenant-scoped (the tenant
 * rides on `X-Tenant-Id` via `authHeaders`).
 */

export type LisReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'NO_ACTION'

export interface LisReviewItem {
  id: string
  status: LisReviewStatus
  testCode: string
  testName: string | null
  orderId: string | null
  mrn: string | null
  /** F final, P preliminary, C corrected. */
  resultStatus: string | null
  notDirectlyOrdered: boolean
  receivedAt: string
  procedureId: string | null
  submissionIds: string[]
  /** Proposals whose change is 'new' or 'changed'. */
  proposalCount: number
  reviewedBy: string | null
  reviewedAt: string | null
  reviewNote: string | null
}

export type LisProposalChange = 'new' | 'changed' | 'same' | 'kept' | 'blocked'

export interface LisReviewProposal {
  /** '' for a row that could not be routed to a form. */
  formDefinitionId: string
  /** '' for a row that could not be routed to a form. */
  formName: string
  submissionId: string | null
  targetFieldId: string
  fieldLabel: string
  sourceLabel: string
  currentValue: unknown
  proposedValue: unknown
  change: LisProposalChange
  /** Stable code, e.g. no_submission | submission_locked | unknown_form. */
  blockedReason: string | null
  /** One sentence for a person; rendered as-is. */
  blockedDetail: string | null
}

export interface LisReviewObservation {
  code: string
  codeText: string | null
  value: string | null
  units: string | null
  referenceRange: string | null
  abnormalFlag: string | null
  status: string | null
}

export interface LisReviewDetail extends LisReviewItem {
  observations: LisReviewObservation[]
  proposals: LisReviewProposal[]
  /**
   * Values that could not be routed to any form field (e.g. no_order,
   * no_mapping), so there is no proposal row to hang them on. `detail` is a
   * sentence for a person; render it as-is.
   */
  failures?: { reason: string; detail: string }[]
  approvable: boolean
}

export interface LisReviewSummary {
  pending: number
  byProcedure: Record<string, number>
  bySubmission: Record<string, number>
  byMrn: Record<string, number>
}

export interface LisReviewListParams {
  status?: LisReviewStatus
  procedureId?: string
  mrn?: string
  submissionId?: string
  limit?: number
  cursor?: string
}

export interface LisReviewPage {
  items: LisReviewItem[]
  nextCursor: string | null
}

export interface LisApproveOutcome {
  status: 'APPROVED'
  applied: number
  unchanged: number
  failures: { reason: string; detail: string }[]
}

export type LisReviewChannel = 'on_screen' | 'email'

export type LisReviewRecipients =
  | { mode: 'editors' }
  | { mode: 'members'; userIds: string[] }
  | { mode: 'roles'; roleIds: string[] }

export interface LisReviewConfig {
  channels: LisReviewChannel[]
  recipients: LisReviewRecipients
  notifyActiveEditor: boolean
}

const ROOT = '/integrations/lis/review'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders(init?.body ? { 'Content-Type': 'application/json' } : undefined),
    ...init,
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

function buildQuery(params: LisReviewListParams): string {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.procedureId) qs.set('procedureId', params.procedureId)
  if (params.mrn) qs.set('mrn', params.mrn)
  if (params.submissionId) qs.set('submissionId', params.submissionId)
  if (params.limit !== undefined) qs.set('limit', String(params.limit))
  if (params.cursor) qs.set('cursor', params.cursor)
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export const lisReviewApi = {
  list: (params: LisReviewListParams = {}) => request<LisReviewPage>(`${ROOT}${buildQuery(params)}`),

  summary: () => request<LisReviewSummary>(`${ROOT}/summary`),

  detail: (id: string) => request<LisReviewDetail>(`${ROOT}/${encodeURIComponent(id)}`),

  approve: (id: string) =>
    request<LisApproveOutcome>(`${ROOT}/${encodeURIComponent(id)}/approve`, { method: 'POST' }),

  reject: (id: string, reason?: string) =>
    request<{ status: 'REJECTED' }>(`${ROOT}/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    }),

  getConfig: () => request<LisReviewConfig>(`${ROOT}/config`),

  putConfig: (config: LisReviewConfig) =>
    request<LisReviewConfig>(`${ROOT}/config`, { method: 'PUT', body: JSON.stringify(config) }),
}
